import { ImageWrapper } from "../components/ImageWrapper";
import { Tooltip } from "../components/Tooltip";
import { User01 } from "../icons/v2-stroke";
import { getEmojiAndBackgroundFromUrl } from "../lib/avatar/utils";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React, { useState } from "react";
export const AVATAR_SIZES = [
    "xxs",
    "xs",
    "sm",
    "md",
    "lg",
    "xl",
    "2xl",
    "auto",
];
export const AVATAR_VARIANTS = ["default", "clickable", "disabled"];
const avatarVariants = cva("s-flex s-flex-shrink-0 s-items-center s-justify-center s-overflow-hidden", {
    variants: {
        size: {
            xxs: "s-h-5 s-w-5",
            xs: "s-h-7 s-w-7",
            sm: "s-h-9 s-w-9",
            md: "s-h-12 s-w-12",
            lg: "s-h-16 s-w-16",
            xl: "s-h-20 s-w-20",
            "2xl": "s-h-36 s-w-36",
            auto: "s-w-full s-relative",
        },
        variant: {
            default: "",
            clickable: "s-cursor-pointer hover:s-filter group-hover:s-filter group-hover:s-brightness-110 hover:s-brightness-110 group-active:s-brightness-90 active:s-brightness-90 s-transition s-duration-200 s-ease-out",
            disabled: "s-opacity-50",
        },
        rounded: {
            true: "s-rounded-full s-ring-[1px] s-ring-border-dark/50 dark:s-ring-border-dark-night/50",
            false: "",
        },
    },
    compoundVariants: [
        {
            rounded: false,
            size: "xxs",
            className: "s-rounded",
        },
        {
            rounded: false,
            size: "xs",
            className: "s-rounded-md",
        },
        {
            rounded: false,
            size: "sm",
            className: "s-rounded-lg",
        },
        {
            rounded: false,
            size: "md",
            className: "s-rounded-xl",
        },
        {
            rounded: false,
            size: "lg",
            className: "s-rounded-2xl",
        },
        {
            rounded: false,
            size: "xl",
            className: "s-rounded-[22px]",
        },
        {
            rounded: false,
            size: "2xl",
            className: "s-rounded-[32px]",
        },
        {
            rounded: false,
            size: "auto",
            className: "s-rounded-[24%]",
        },
    ],
    defaultVariants: {
        size: "md",
        variant: "default",
        rounded: false,
    },
});
const textVariants = cva("s-select-none s-font-semibold", {
    variants: {
        size: {
            xxs: "s-text-[10px]",
            xs: "s-text-xs",
            sm: "s-text-sm",
            md: "s-text-base",
            lg: "s-text-3xl",
            xl: "s-text-5xl",
            "2xl": "s-text-7xl",
            auto: "s-text-xl",
        },
    },
    defaultVariants: {
        size: "md",
    },
});
const getColor = (name) => {
    if (/\+/.test(name)) {
        return "s-bg-primary-300";
    }
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
        "s-bg-blue-300",
        "s-bg-violet-300",
        "s-bg-pink-300",
        "s-bg-red-300",
        "s-bg-orange-300",
        "s-bg-golden-300",
        "s-bg-lime-300",
        "s-bg-emerald-300",
    ];
    return colors[Math.abs(hash) % colors.length];
};
const getTextVariant = (name) => {
    if (/\+/.test(name)) {
        return "s-text-muted-foreground";
    }
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const txtColors = [
        "s-text-blue-700",
        "s-text-violet-700",
        "s-text-pink-700",
        "s-text-red-700",
        "s-text-orange-700",
        "s-text-golden-700",
        "s-text-lime-700",
        "s-text-emerald-700",
    ];
    return txtColors[Math.abs(hash) % txtColors.length];
};
export function Avatar({ size, name, emoji, visual, onClick, clickable = false, busy = false, isRounded = false, backgroundColor, hexBgColor, disabled = false, className, icon, iconColor = "s-text-foreground", }) {
    const normalizedVisual = visual === "" ? null : visual;
    const emojiInfos = typeof normalizedVisual === "string" &&
        getEmojiAndBackgroundFromUrl(normalizedVisual);
    const backgroundColorToUse = emojiInfos
        ? emojiInfos.backgroundColor
        : backgroundColor;
    const emojiToUse = emojiInfos ? emojiInfos.skinEmoji : emoji;
    const visualToUse = emojiInfos ? null : normalizedVisual;
    const variant = disabled
        ? "disabled"
        : (onClick || clickable) && !busy
            ? "clickable"
            : "default";
    const isImageVisual = typeof visualToUse === "string";
    const bgColorClass = isImageVisual || hexBgColor
        ? ""
        : (backgroundColorToUse !== null && backgroundColorToUse !== void 0 ? backgroundColorToUse : (name ? getColor(name) : "s-bg-muted-background"));
    return (React.createElement("div", { className: cn(!isImageVisual && "s-border s-border-primary-800/10", avatarVariants({
            size,
            variant,
            rounded: isRounded,
        }), busy ? "s-animate-breathing s-cursor-default" : "", bgColorClass, className), style: hexBgColor && !isImageVisual
            ? { backgroundColor: hexBgColor }
            : undefined },
        size === "auto" && React.createElement("div", { style: { paddingBottom: "100%" } }),
        typeof visualToUse === "string" ? (React.createElement(ImageWrapper, { src: visualToUse, alt: name, className: cn(avatarVariants({ size }), "s-object-cover s-object-center") })) : visualToUse ? (visualToUse) : icon ? (React.createElement(icon, {
            className: cn("s-h-1/2 s-w-1/2", iconColor),
        })) : emojiToUse ? (React.createElement("span", { className: textVariants({ size }) }, emojiToUse)) : name ? (React.createElement("span", { className: cn(textVariants({ size }), getTextVariant(name)) }, /\+/.test(name) ? name : name[0].toUpperCase())) : (React.createElement(User01, { className: "s-h-1/2 s-w-1/2 s-text-foreground s-opacity-20" }))));
}
const AVATAR_STACK_SIZES = ["xs", "sm", "md"];
const sizeClassesPx = {
    xs: 24,
    sm: 32,
    md: 40,
};
Avatar.Stack = function ({ avatars, nbVisibleItems, size = "sm", hasMagnifier = true, tooltipTriggerAsChild = false, orientation = "horizontal", onTop = "last", }) {
    const [isHovered, setIsHovered] = useState(false);
    // Get visible avatars and calculate remaining count
    const shouldShowAll = !nbVisibleItems || avatars.length <= nbVisibleItems;
    const isFirstOnTop = onTop === "first";
    const maxVisible = shouldShowAll
        ? avatars.length
        : isFirstOnTop
            ? nbVisibleItems
            : nbVisibleItems - 1;
    const visibleAvatars = isFirstOnTop
        ? avatars.slice(0, maxVisible).reverse()
        : avatars.slice(0, maxVisible);
    const remainingCount = shouldShowAll || isFirstOnTop ? 0 : avatars.length - maxVisible;
    // Get all names for tooltip
    const avatarNames = avatars
        .filter((avatar) => avatar.name)
        .map((avatar) => avatar.name);
    const tooltipLabel = avatarNames.join(", ");
    const sizeSetting = {
        marginLeft: 0,
        widthHovered: sizeClassesPx[size] * 0.6,
        width: sizeClassesPx[size] * 0.25,
        heightHovered: sizeClassesPx[size] * 0.6,
        height: sizeClassesPx[size] * 0.25,
    };
    const collapsedWidth = sizeSetting.width *
        (visibleAvatars.length + Number(Boolean(remainingCount))) +
        (sizeClassesPx[size] - sizeSetting.width);
    const openedWidth = sizeSetting.widthHovered *
        (visibleAvatars.length + Number(Boolean(remainingCount))) +
        (sizeClassesPx[size] - sizeSetting.widthHovered);
    const collapsedHeight = sizeSetting.height *
        (visibleAvatars.length + Number(Boolean(remainingCount))) +
        (sizeClassesPx[size] - sizeSetting.height);
    const openedHeight = sizeSetting.heightHovered *
        (visibleAvatars.length + Number(Boolean(remainingCount))) +
        (sizeClassesPx[size] - sizeSetting.heightHovered);
    const transitionSettings = orientation === "vertical"
        ? "height 200ms ease-out"
        : "width 200ms ease-out";
    return (React.createElement(Tooltip, { label: tooltipLabel, tooltipTriggerAsChild: tooltipTriggerAsChild, trigger: React.createElement(React.Fragment, null,
            React.createElement("div", { className: cn("s-flex", onTop === "first"
                    ? orientation === "vertical"
                        ? "s-flex-col-reverse s-justify-end"
                        : "s-flex-row-reverse s-justify-end"
                    : orientation === "vertical"
                        ? "s-flex-col"
                        : "s-flex-row"), onMouseEnter: () => visibleAvatars.length > 1 && setIsHovered(true), onMouseLeave: () => visibleAvatars.length > 1 && setIsHovered(false), style: {
                    [orientation === "vertical" ? "height" : "width"]: `${isHovered
                        ? orientation === "vertical"
                            ? openedHeight
                            : openedWidth
                        : orientation === "vertical"
                            ? collapsedHeight
                            : collapsedWidth}px`,
                    transition: transitionSettings,
                } },
                visibleAvatars.map((avatarProps, i) => (React.createElement("div", { key: i, className: "s-cursor-pointer", style: {
                        [orientation === "vertical" ? "height" : "width"]: isHovered
                            ? orientation === "vertical"
                                ? sizeSetting.heightHovered
                                : sizeSetting.widthHovered
                            : orientation === "vertical"
                                ? sizeSetting.height
                                : sizeSetting.width,
                        transition: transitionSettings,
                    } }, hasMagnifier ? (React.createElement("div", { style: {
                        transform: `scale(${onTop === "first"
                            ? 1 -
                                (visibleAvatars.length +
                                    (remainingCount > 0 ? 1 : 0) -
                                    1 -
                                    i) *
                                    0.06
                            : 1 -
                                (visibleAvatars.length +
                                    (remainingCount > 0 ? 1 : 0) -
                                    i) *
                                    0.06})`,
                    } },
                    React.createElement(Avatar, { ...avatarProps, size: size }))) : (React.createElement(Avatar, { ...avatarProps, size: size }))))),
                remainingCount > 0 && (React.createElement("div", { className: "s-cursor-pointer", style: {
                        [orientation === "vertical" ? "height" : "width"]: isHovered
                            ? orientation === "vertical"
                                ? sizeSetting.heightHovered
                                : sizeSetting.widthHovered
                            : orientation === "vertical"
                                ? sizeSetting.height
                                : sizeSetting.width,
                        transition: transitionSettings,
                    } }, hasMagnifier ? (React.createElement("div", { style: {
                        transform: `scale(${onTop === "first" ? 1 : 1 - 0.06})`,
                    } },
                    React.createElement(Avatar, { size: size, name: Number(remainingCount) < 10
                            ? `+${remainingCount}`
                            : "9+" }))) : (React.createElement(Avatar, { size: size, name: Number(remainingCount) < 10 ? `+${remainingCount}` : "9+" })))))) }));
};
//# sourceMappingURL=Avatar.js.map